import { ArticleHeading2, Chips, type IChipsOption, UIHeading1 } from '@cian/ui-kit';

import { type FC, useCallback, useMemo, useState } from 'react';

import { DATA_AS_NAV_TAB_ATTR } from 'shared/components/Header/internal/utils/dataAsNavTabAttr';
import { Layout } from 'shared/components/Layout';

import { SmartHomeCard } from './internal/components/SmartHomeCard';
import { type SmartHomeCategoryId, type SmartHomeScheme } from './internal/types';

import styles from './SmartHome.css';

type SmartHomeProps = {
  scheme: SmartHomeScheme;
};

export const SmartHome: FC<SmartHomeProps> = ({ scheme }) => {
  const [activeCategoryId, setActiveCategoryId] = useState<SmartHomeCategoryId>(scheme.categories[0].id);

  const chipsOptions = useMemo<IChipsOption<SmartHomeCategoryId>[]>(
    () => scheme.categories.map(category => category.chipsOption),
    [scheme.categories],
  );

  const activeCategory = useMemo(
    () => scheme.categories.find(category => category.id === activeCategoryId) ?? scheme.categories[0],
    [activeCategoryId, scheme.categories],
  );

  const handleCategoryChange = useCallback((selectedCategoryIds: SmartHomeCategoryId[]): void => {
    const nextCategoryId = selectedCategoryIds[0];

    if (nextCategoryId) {
      setActiveCategoryId(nextCategoryId);
    }
  }, []);

  return (
    <section
      data-testid="smart-home"
      data-active-category={activeCategory.id}
      id={scheme.id}
      {...{ [DATA_AS_NAV_TAB_ATTR]: scheme.sectionTabTitle }}
      className={styles['section']}
    >
      <img
        alt=""
        aria-hidden="true"
        className={styles['background-image']}
        decoding="async"
        loading="lazy"
        src={activeCategory.backgroundImageSrc}
      />
      <div className={styles['overlay']} />
      <Layout stretch>
        <div className={styles['content']}>
          <header className={styles['header']}>
            <UIHeading1>{scheme.title}</UIHeading1>
            <div aria-label="Категории умного дома" className={styles['chips']} role="group">
              <Chips<SmartHomeCategoryId>
                dataTestId="smart-home-category-chips"
                options={chipsOptions}
                padded={false}
                selectType="oneOf"
                value={[activeCategoryId]}
                onChange={handleCategoryChange}
              />
            </div>
          </header>

          <div className={styles['description']}>
            {scheme.categories.map(category => (
              <div
                className={styles['description-text']}
                data-testid={category.id === activeCategoryId ? 'smart-home-description' : undefined}
                hidden={category.id !== activeCategoryId}
                key={category.id}
              >
                {category.description.map((description, index) =>
                  description ? (
                    <ArticleHeading2 fontWeight="normal" key={`${category.id}-description-${index}`}>
                      {description}{' '}
                    </ArticleHeading2>
                  ) : (
                    <br key={`${category.id}-description-${index}`} />
                  ),
                )}
              </div>
            ))}
          </div>

          <div className={styles['card-panels']}>
            {scheme.categories.map(category => (
              <article
                className={styles['card-panel']}
                data-testid={`smart-home-category-panel-${category.id}`}
                hidden={category.id !== activeCategoryId}
                key={category.id}
              >
                <div className={styles['cards']}>
                  {category.cards.map(card => (
                    <SmartHomeCard card={card} key={card.title} />
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </Layout>
    </section>
  );
};
